/**
 * M3 WORLD B1: signal-source system. Pure, deterministic, engine-agnostic (no
 * Three.js, no Math.random, no Date.now, no DOM). Mirrors npc.ts's hash(seed,i)
 * seeding so the same seed always yields the same pocket of activity — the world
 * is "localized": seeded off a QUANTIZED player position so signals are stable as
 * you fly, and re-derivable on load (positions are NOT saved; ids are).
 *
 * A SignalSource is a droppable point of interest in supercruise — a second drop
 * target parallel to a planet surface (see travel/supercruise.ts canDropToSignal).
 * Dropping in resolves SignalContents: a named derelict + drift-log, scoopable
 * salvage, hostiles/victims (injected into the client's existing Sim.npcs+Sim.hp),
 * or a memorial site. Kinds fit the Black Marble sombre theme.
 *
 * B-NARRATIVE: every derelict carries a NAMED hull + a readable drift-log authored
 * from RLM/specs/narrative/ (the Silence of 2049, the Cenotaph, Forty-Seven
 * Seconds, the Roll of the Dead, the five Powers). The lore data tables live HERE,
 * inside this pure module, so the narrative is deterministic and replayable.
 */
import { add, sub, scale, length, type Vec3 } from './vec3.js';
import { FACTION_DISPLAY, factionDisplayName, type FactionId } from './faction.js';
import { AMBIENT_FACTIONS, ambientEnemyTypesForFaction } from './ship.js';

/** Sombre signal kinds. derelict/distress/wreckfield/ambush/mining/anomaly/memorial. */
export type SignalKind =
  | 'derelict' // a named, dead hull with a readable drift-log + salvage
  | 'distress' // a victim under attack; rescue or fail (adds to the Roll)
  | 'wreckfield' // scattered salvage, no story, pure scavenging
  | 'ambush' // hostiles waiting; rare force-drop interdiction
  | 'mining' // mineable nodes, peaceful
  | 'anomaly' // an unexplained reading (the Silence's long shadow)
  | 'memorial'; // a Cenotaph-referencing site; non-combat, reverent

export interface SignalSource {
  id: string; // 'sig-<seed>-<i>' (persistent) or 'story-<name>' (scripted)
  kind: SignalKind;
  pos: Vec3; // Float64 world position (km)
  markerOffsetKm?: Vec3; // optional presentation anchor relative to pos (e.g. a panel on a surface)
  threatLevel: number; // 0..5 (0 = safe, 5 = lethal); HUD threat readout
  radiusKm: number; // drop-in proximity radius (parallels a body surface band)
  seed: number; // deterministic content seed (hash inputs)
  decayT: number; // seconds of life remaining before the signal fades + culls
  discovered: boolean; // set true once the player drops in (persisted by id)
  title: string; // HUD label, e.g. 'UNKNOWN SIGNAL' or a named derelict
  /** SHIP-EXPANSION: owning faction of this pocket — hostiles spawn from this
   * faction's ambient pool (gating the Quiet + bosses OUT). Absent ⇒ legacy mix. */
  faction?: FactionId;
  /** SHIP-EXPANSION: force every hostile to a specific hull (scripted story
   * encounters, e.g. the Black Cutter flying the `hellebore`). Overrides faction. */
  enemyType?: string;
  /** CAMPAIGN W1: authored carved record — when present it overrides the
   * procedural lore tables in carvedRecordFor, so campaign beats get the full
   * VERIFY/CONTRADICTION ledger flow on their own names. */
  record?: { name: string; log: string | null };
}

/** A single scoopable canister or mineable node at a resolved signal. */
export interface SalvageNode {
  id: string; // 'salv-<sig>-<i>'
  pos: Vec3; // world position (km)
  commodityId: string; // economy.ts commodity id (reuses the trade loop)
  qty: number; // units granted on scoop
}

/** A combatant slot to be injected into the client's Sim.npcs + Sim.hp. */
export interface EncounterShip {
  id: string; // 'enc-<sig>-<i>' (vs persistent 'npc-*') for clean despawn
  shipType: string; // hostile fleet: 'raider'|'interdictor'|'patrol'|'salvager'
  pos: Vec3; // world position (km)
  faction: string; // B-NARRATIVE flavor tag (see FACTIONS)
  hostile: boolean; // true = attacks the player; false = victim to protect
}

/** Resolved interior of a dropped-into signal. */
export interface SignalContents {
  derelictTitle: string | null; // named hull (B-NARRATIVE), null if none
  driftLog: string | null; // readable last-transmission, null if none
  salvage: SalvageNode[];
  hostiles: EncounterShip[];
  victims: EncounterShip[];
  mineNodes: SalvageNode[];
}

// --- Numeric constants (documented; tunable, ship as-is). ---
/** Default signal lifetime before it decays and is culled (seconds). */
export const SIGNAL_DECAY_SEC = 240;
/**
 * Drop-in proximity band radius (km). Sized as a generous Elite-style USS drop
 * window: ~30% of the ambient spawn spread so a cruising approach reliably enters
 * the band with seconds of lead time (vs the old 1_500 km pinhole, which was
 * effectively impossible to hit by hand across the spawn distance).
 */
export const SIGNAL_RADIUS_KM = 120_000;
/** Quantization grid for position-seeding (km) — signals are stable within a cell. */
export const SIGNAL_SEED_GRID_KM = 200_000;
/**
 * Spread (km) of seeded signals around the seeding anchor. Pulled in from the old
 * 30 MKM (multi-MKM encounters were unreachable) to a few hundred thousand km so
 * an ambient signal is routinely within practical cruise reach. SIGNAL_MIN_SPAWN_KM
 * guarantees at least one signal lands close enough to reach within ~a minute.
 */
export const SIGNAL_FIELD_SPREAD_KM = 400_000;
/**
 * The first ambient signal (i=0) is clamped to spawn within this distance of the
 * anchor so engaging supercruise always yields a reachable encounter quickly. At a
 * mid throttle this is a few seconds to tens of seconds of cruise.
 */
export const SIGNAL_MIN_SPAWN_KM = 80_000;
/**
 * Seconds of lead time the drop band gains from closing speed. canDropToSignal
 * widens the band by closingSpeedKmS * this, so a fast approach still catches the
 * window (mirrors how supercruise bleeds speed near a body). 0 closing => raw band.
 */
export const SIGNAL_DROP_LEAD_SEC = 0.6;

/** Deterministic integer hash from (seed, i) — copied verbatim from npc.ts. */
const hash = (seed: number, i: number): number =>
  (Math.imul(seed >>> 0, 2654435761) ^ Math.imul(i >>> 0, 40503)) >>> 0;

/** Deterministic 0..1 fraction from a hash channel. */
const frac = (seed: number, i: number): number => (hash(seed, i) % 100000) / 100000;

/** Quantize one axis to the seed grid so nearby positions share a signal field. */
const quantizeAxis = (v: number): number =>
  Math.round(v / SIGNAL_SEED_GRID_KM) * SIGNAL_SEED_GRID_KM;

/** Position seed: stable across small motion (quantized), unique per grid cell. */
export const positionSeed = (pos: Vec3): number => {
  const qx = quantizeAxis(pos.x) >>> 0;
  const qy = quantizeAxis(pos.y) >>> 0;
  const qz = quantizeAxis(pos.z) >>> 0;
  return hash(hash(qx, qy), qz) >>> 0;
};

// --- B-NARRATIVE: lore tables (pure data; sourced from RLM/specs/narrative). ---

/**
 * The five Powers + cross-cutting orders (03-factions.md), as flavor tags.
 * SHIP-EXPANSION: reconciled with the canonical faction model — the five Powers'
 * strings are sourced from FACTION_DISPLAY (byte-identical to the originals), so
 * factionFor output is unchanged; 'the Reflected' stays as a non-fleet order tag.
 */
export const FACTIONS = [
  FACTION_DISPLAY.concord,
  FACTION_DISPLAY.aresian,
  FACTION_DISPLAY.belt,
  FACTION_DISPLAY.jovian,
  FACTION_DISPLAY.outer,
  'the Reflected',
] as const;

/** Named derelict hulls with readable drift-logs (the Silence, the Reach, callsigns). */
interface DerelictLore {
  title: string;
  log: string | null; // memorials carry a title but no drift-log
}
const DERELICTS: DerelictLore[] = [
  {
    title: 'HALIS VORN — Reach-era survey cutter',
    log: 'last transmission: "...past the heliopause it is not empty. tell the Guild the wall is wrong. tell them HALIS VORN is not dead." carrier lost 47s after.',
  },
  {
    title: 'KESTREL-NINE — Guild service capsule',
    log: 'drift-log: "micrometeoroid, one in ten thousand. pressure gone. Vey — finish the name. carve true." no further signal.',
  },
  {
    title: 'do tūm — Spún long-hauler',
    log: 'Spún drift-log: "we remember every name the inner system forgot. the Cenotaph launders the sin. cut our air and we strike. — the Hollow, Ceres."',
  },
  {
    title: 'VIGIL ASHFALL — Concord destroyer, decommissioned',
    log: 'log: "names of the Silence dead painted along this hull. we were very good and there were never enough of us. the long quiet holds."',
  },
  {
    title: 'TWO-BOWL — Reflected pilgrim skiff',
    log: 'log, repeating: "I am the one who did it. make it small. make it few. pay it back when you can." a single steel band drifts in the cabin.',
  },
  {
    title: 'FORTY-SEVEN — anomaly-survey drone, Outer Concordance',
    log: 'fragment: "03:14:22 UTC, 11 June 2049. every human asleep for forty-seven seconds. 1.2 billion did not wake. we are still listening for what spoke."',
  },
];

/** Memorial site inscriptions — Cenotaph / Forty-Seven Seconds references. */
const MEMORIALS: string[] = [
  'CENOTAPH WAYPOINT — "Monument to the Forty-Seven Seconds. 140 km of carved names. unfinished. it will never be finished."',
  'ROLL OF THE DEAD, OUTER MARKER — "we owe the people not yet found. the writing continues. carve true."',
  'SILENCE MEMORIAL — "for the 1.2 billion who did not wake. a black circle with a mirrored half."',
];

/** Anomaly readings — the Silence's long shadow (no answers, only questions). */
const ANOMALIES: string[] = [
  'ANOMALY — flat EEG signature, no source. matches the 2049 sensor logs. recurrence?',
  'ANOMALY — a mirrored-half waveform. every survivor drew this shape. nobody can read it.',
  'ANOMALY — the heliopause is pushing back harder here. the edge of the known world.',
];

/** Deterministic pick from a table by a hash channel. */
const pick = <T>(table: readonly T[], seed: number, i: number): T =>
  table[hash(seed, i) % table.length] as T;

/** Deterministic faction tag for a content seed. */
export const factionFor = (seed: number, i: number): string => pick(FACTIONS, seed, i);

/** Deterministic surface marker offset pointing back toward the anchor body. */
export const surfaceMarkerOffset = (anchorOffset: Vec3, radiusKm: number): Vec3 =>
  scale(anchorOffset, -radiusKm / (length(anchorOffset) || 1));

/** Presentation-space marker position (surface panel when authored, centre otherwise). */
export const signalMarkerPos = (sig: SignalSource): Vec3 =>
  sig.markerOffsetKm ? add(sig.pos, sig.markerOffsetKm) : sig.pos;

// --- Kind selection: weighted toward the sombre, story-bearing kinds. ---
const KIND_TABLE: SignalKind[] = [
  'derelict',
  'derelict',
  'distress',
  'distress',
  'wreckfield',
  'mining',
  'anomaly',
  'memorial',
  'ambush',
];

/** Deterministic kind for a (seed,i), optionally biased toward a kind. */
const kindFor = (seed: number, i: number): SignalKind =>
  KIND_TABLE[hash(seed, i * 13 + 1) % KIND_TABLE.length] as SignalKind;

/** Threat by kind: combat kinds rate higher; reverent/peaceful kinds rate 0. */
const threatFor = (kind: SignalKind, seed: number, i: number): number => {
  if (kind === 'ambush') return 4 + (hash(seed, i) % 2); // 4-5
  if (kind === 'distress') return 1 + (hash(seed, i) % 3); // 1-3 (the hunters)
  if (kind === 'derelict' || kind === 'wreckfield') return hash(seed, i) % 2; // 0-1
  return 0; // mining / anomaly / memorial are safe
};

/** Default HUD title by kind (named derelicts/memorials resolve their own later). */
const titleFor = (kind: SignalKind): string => {
  switch (kind) {
    case 'memorial':
      return 'MEMORIAL BEACON';
    case 'distress':
      return 'DISTRESS CALL';
    case 'ambush':
      return 'UNKNOWN SIGNAL';
    case 'anomaly':
      return 'ANOMALOUS READING';
    default:
      return 'UNIDENTIFIED SIGNAL';
  }
};

/**
 * Deterministic spread offset for signal `i` around an anchor. The first signal
 * (i=0) is pulled to within SIGNAL_MIN_SPAWN_KM so engaging supercruise always
 * yields a reachable encounter quickly; the rest spread out to SIGNAL_FIELD_SPREAD_KM.
 * Pure (hash-derived direction; no randomness).
 */
const signalOffset = (baseSeed: number, i: number): Vec3 => {
  const dir = {
    x: frac(baseSeed, i * 7 + 1) - 0.5,
    y: (frac(baseSeed, i * 7 + 2) - 0.5) * 0.3, // flatter in Y (ecliptic-ish)
    z: frac(baseSeed, i * 7 + 3) - 0.5,
  };
  if (i === 0) {
    const len = length(dir) || 1;
    return scale(dir, SIGNAL_MIN_SPAWN_KM / len); // guaranteed-reachable opener
  }
  return scale(dir, SIGNAL_FIELD_SPREAD_KM);
};

/**
 * Seed a small field of signals around an anchor position. Deterministic for a
 * given (anchorPos, count): seeds off the QUANTIZED anchor so the field is stable
 * as the player drifts within a grid cell, and re-derivable on load. Each signal
 * is offset from the anchor by a hash-spread vector. Pure. Positions are NOT saved.
 */
export const makeSignals = (anchorPos: Vec3, count: number, biasDir?: Vec3): SignalSource[] => {
  const baseSeed = positionSeed(anchorPos);
  // SHIP-EXPANSION: the field's quantized cell is one faction's territory, so a
  // region fields faction-correct hostiles. Deterministic; never the Quiet.
  const faction = regionFaction(baseSeed);
  const out: SignalSource[] = [];
  for (let i = 0; i < count; i++) {
    const kind = kindFor(baseSeed, i);
    const off = signalOffset(baseSeed, i);
    const biased = biasDir && i !== 0 && (kind === 'distress' || kind === 'ambush')
      ? add(off, scale(biasDir, SIGNAL_FIELD_SPREAD_KM * 0.4))
      : off;
    out.push({
      id: `sig-${baseSeed}-${i}`,
      kind,
      pos: add(anchorPos, biased),
      threatLevel: threatFor(kind, baseSeed, i),
      radiusKm: SIGNAL_RADIUS_KM,
      seed: hash(baseSeed, i * 31 + 17),
      decayT: SIGNAL_DECAY_SEC,
      discovered: false,
      title: titleFor(kind),
      faction,
    });
  }
  return out;
};

/** Deterministic ambient-territory faction for a region seed (never the Quiet). */
export const regionFaction = (seed: number): FactionId =>
  AMBIENT_FACTIONS[hash(seed, 0xa9) % AMBIENT_FACTIONS.length] as FactionId;

/**
 * Decay a signal by dt. Pure: returns a NEW SignalSource with a reduced decayT
 * (clamped at 0). Use signalExpired to decide culling. Discovered signals do NOT
 * decay (they persist so the player can return; the client gates this).
 */
export const stepSignalDecay = (sig: SignalSource, dt: number): SignalSource => {
  if (sig.discovered) return sig;
  return { ...sig, decayT: Math.max(0, sig.decayT - dt) };
};

/** A signal is culled once its decay timer runs out (and it was never reached). */
export const signalExpired = (sig: SignalSource): boolean =>
  !sig.discovered && sig.decayT <= 0;

/**
 * How many encounter ships a kind spawns (deterministic by seed + threatLevel).
 * Ambush hostiles scale up with threat: base 2 + floor(threatLevel/2) + 1-bit
 * jitter, capped at 6. Distress keeps 1 victim and scales hunters similarly.
 */
const shipCountFor = (
  kind: SignalKind,
  seed: number,
  threatLevel = 0,
): { hostiles: number; victims: number } => {
  if (kind === 'ambush') {
    const base = 2 + Math.floor(threatLevel / 2); // 2 at thr=0, 4 at thr=4-5
    const jitter = hash(seed, 1) % 2; // 0 or 1 deterministic jitter
    return { hostiles: Math.min(6, base + jitter), victims: 0 };
  }
  if (kind === 'distress') {
    const base = 1 + Math.floor(threatLevel / 2); // 1 at thr=0, 2 at thr=2-3
    const jitter = hash(seed, 2) % 2;
    return { hostiles: Math.min(4, base + jitter), victims: 1 };
  }
  return { hostiles: 0, victims: 0 };
};

/**
 * WAVE 4: deterministic hostile-fleet type assignment. The encounter NPCs fly the
 * DISTINCT enemy ships (raider/interdictor/patrol/salvager), never the player fleet.
 * Non-combatant victims fly the bulky salvager (the prey being raided). Hostiles
 * scale by threat: low threat fields lean patrols + raiders; mid threat mixes in
 * the interdictor; high threat anchors an armored interdictor amid a raider swarm.
 * Pure (hash-derived spread off the signal seed; no Math.random).
 */
const enemyShipType = (sig: SignalSource, i: number, hostile: boolean): string => {
  // SHIP-EXPANSION: a scripted hull override wins (e.g. the Black Cutter = hellebore).
  if (hostile && sig.enemyType) return sig.enemyType;
  if (!hostile) return 'salvager'; // victims are non-combatant salvagers under attack
  // SHIP-EXPANSION: faction-scoped spawning — a faction-tagged pocket draws from
  // that faction's ambient hostile pool (the Quiet + bosses are gated out there).
  if (sig.faction) {
    const pool = ambientEnemyTypesForFaction(sig.faction);
    return pool[(hash(sig.seed, i * 5 + 3) >>> 4) % pool.length] as string;
  }
  const r = (hash(sig.seed, i * 5 + 3) >>> 4) % 3; // 0-2 upper-bits spread
  const threat = sig.threatLevel;
  if (threat <= 1) return (i + r) % 2 === 0 ? 'patrol' : 'raider';
  if (threat <= 3) return (['raider', 'patrol', 'interdictor'] as const)[(i + r) % 3] as string;
  if (i === 0) return 'interdictor'; // high threat: an armored anchor leads the swarm
  return r === 0 ? 'interdictor' : 'raider';
};

/**
 * Build an EncounterShip at a hash-spread offset from the signal centre. Its
 * shipType is the distinct hostile-fleet selection (enemyShipType) so a pocket
 * fields visibly different enemy ships that vary by threat — never the player fleet.
 */
const makeEncounter = (
  prefix: string,
  sig: SignalSource,
  i: number,
  hostile: boolean,
): EncounterShip => {
  return {
    id: `${prefix}-${sig.id}-${i}`,
    shipType: enemyShipType(sig, i, hostile),
    pos: add(
      sig.pos,
      scale(
        {
          x: frac(sig.seed, i * 9 + 1) - 0.5,
          y: frac(sig.seed, i * 9 + 2) - 0.5,
          z: frac(sig.seed, i * 9 + 3) - 0.5,
        },
        80, // tight cluster (km) so the pocket reads as dense
      ),
    ),
    // SHIP-EXPANSION: a faction-tagged pocket labels its ships with that faction;
    // legacy untagged pockets keep the procedural flavor-tag selection.
    faction: sig.faction ? factionDisplayName(sig.faction) : factionFor(sig.seed, i * 3 + 5),
    hostile,
  };
};

/** Build the salvage canisters for a kind (derelict/wreckfield/mining). */
const makeSalvage = (sig: SignalSource, count: number, isMine: boolean): SalvageNode[] => {
  const commodities = isMine
    ? ['water-ice', 'ore']
    : ['electronics', 'ore', 'medicine', 'luxuries'];
  const out: SalvageNode[] = [];
  for (let i = 0; i < count; i++) {
    out.push({
      id: `salv-${sig.id}-${i}`,
      pos: add(
        sig.pos,
        scale(
          {
            x: frac(sig.seed, i * 11 + 4) - 0.5,
            y: frac(sig.seed, i * 11 + 5) - 0.5,
            z: frac(sig.seed, i * 11 + 6) - 0.5,
          },
          60,
        ),
      ),
      commodityId: commodities[hash(sig.seed, i * 7) % commodities.length] as string,
      qty: 1 + (hash(sig.seed, i * 3 + 1) % 4),
    });
  }
  return out;
};

/** Number of salvage/mine nodes a kind yields. */
const salvageCountFor = (kind: SignalKind, seed: number): number => {
  if (kind === 'wreckfield') return 4 + (hash(seed, 7) % 4); // 4-7
  if (kind === 'derelict') return 2 + (hash(seed, 8) % 3); // 2-4
  if (kind === 'mining') return 3 + (hash(seed, 9) % 4); // 3-6
  return 0;
};

/** Resolve the narrative title + drift-log for a derelict (B-NARRATIVE). */
const resolveDerelict = (sig: SignalSource): DerelictLore | null => {
  if (sig.kind === 'derelict' || sig.kind === 'distress') {
    return pick(DERELICTS, sig.seed, 0);
  }
  if (sig.kind === 'memorial') {
    return { title: pick(MEMORIALS, sig.seed, 0), log: null };
  }
  if (sig.kind === 'anomaly') {
    return { title: 'ANOMALY', log: pick(ANOMALIES, sig.seed, 0) };
  }
  return null;
};

/**
 * AAA-01: the carved RECORD a signal presents for verification — the name on the
 * wall + its drift-log claim (if any). Single deterministic source of carved
 * content for verification.ts. Memorials carry an inscription (no log); derelicts/
 * distress carry a named hull + drift-log; anomalies carry the reading as the log.
 */
export const carvedRecordFor = (sig: SignalSource): { name: string; log: string | null } => {
  if (sig.record) return sig.record; // CAMPAIGN W1: authored override wins
  const lore = resolveDerelict(sig);
  if (lore) return { name: lore.title, log: lore.log };
  return { name: sig.title, log: null };
};

/**
 * Split a hostile list into 1-3 waves scaled by threatLevel. Deterministic.
 * Low threat (0-1) = 1 wave; mid threat (2-3) = 2 waves; high (4-5) = 3 waves.
 * Every wave is non-empty; the union of all waves equals the full hostile set.
 * Victims always accompany wave 1 (passed in as waveOneExtras).
 *
 * Exported so the client can drive wave-by-wave spawning on drop-in.
 */
export const resolveWaves = (sig: SignalSource): EncounterShip[][] => {
  const ships = shipCountFor(sig.kind, sig.seed, sig.threatLevel);
  if (ships.hostiles === 0) return [];
  const hostiles: EncounterShip[] = [];
  for (let i = 0; i < ships.hostiles; i++) hostiles.push(makeEncounter('enc', sig, i, true));
  // Wave count: 1 for threat 0-1, 2 for threat 2-3, 3 for threat 4-5.
  const waveCount = sig.threatLevel <= 1 ? 1 : sig.threatLevel <= 3 ? 2 : 3;
  if (waveCount === 1) return [hostiles];
  const waves: EncounterShip[][] = Array.from({ length: waveCount }, () => []);
  for (let i = 0; i < hostiles.length; i++) {
    const w = i % waveCount; // round-robin distribution keeps every wave non-empty
    waves[w]!.push(hostiles[i]!);
  }
  // Guard: if any wave ended up empty (shouldn't happen with round-robin), merge it up.
  return waves.filter((w) => w.length > 0);
};

/**
 * Resolve the full interior of a signal on drop-in. Pure + deterministic by the
 * signal's seed: same signal => same hostiles, victims, salvage, name, and log.
 * The client injects hostiles/victims into Sim.npcs+Sim.hp (tagged enc-*) and
 * pools the salvage canisters. B-NARRATIVE: named derelict + drift-log included.
 * Hostiles are the union of all resolveWaves() waves (backward-compat flat list).
 */
export const resolveContents = (sig: SignalSource): SignalContents => {
  const ships = shipCountFor(sig.kind, sig.seed, sig.threatLevel);
  const isMine = sig.kind === 'mining';
  const salvageCount = salvageCountFor(sig.kind, sig.seed);
  const lore = resolveDerelict(sig);
  const hostiles: EncounterShip[] = [];
  for (let i = 0; i < ships.hostiles; i++) hostiles.push(makeEncounter('enc', sig, i, true));
  const victims: EncounterShip[] = [];
  for (let i = 0; i < ships.victims; i++) victims.push(makeEncounter('enc', sig, 100 + i, false));
  const nodes = makeSalvage(sig, salvageCount, isMine);
  return {
    // CAMPAIGN polish: an authored carved record owns the on-drop prose too,
    // so the drift-log the player reads IS the record the ledger verifies.
    derelictTitle: sig.record ? sig.record.name : (lore?.title ?? null),
    driftLog: sig.record ? sig.record.log : (lore?.log ?? null),
    salvage: isMine ? [] : nodes,
    hostiles,
    victims,
    mineNodes: isMine ? nodes : [],
  };
};

/**
 * AAA-13 (debug): re-stamp a signal to a forced kind, re-deriving threat + title
 * from the same pure fns so it stays internally consistent. Never mutates the
 * KIND_TABLE / kindFor selection — only the client's debug `?signal=` path calls it.
 */
export const forceKind = (sig: SignalSource, kind: SignalKind): SignalSource => ({
  ...sig,
  kind,
  threatLevel: threatFor(kind, sig.seed, 0),
  title: titleFor(kind),
});

/** Nearest non-expired signal to a position (and its distance), or null. Pure. */
export const nearestSignal = (
  signals: readonly SignalSource[],
  pos: Vec3,
): { signal: SignalSource; distanceKm: number } | null => {
  let best: { signal: SignalSource; distanceKm: number } | null = null;
  for (const s of signals) {
    if (signalExpired(s)) continue;
    const d = length(sub(s.pos, pos));
    if (!best || d < best.distanceKm) best = { signal: s, distanceKm: d };
  }
  return best;
};

/**
 * M3 WORLD B2: PURE drop-in band test, INDEPENDENT of any cruise state. True when
 * the ship is inside a signal's proximity radius, widened by closing speed *
 * SIGNAL_DROP_LEAD_SEC so a fast approach still catches the window. This is the
 * drop DECISION predicate: it must NOT depend on whether supercruise is engaged,
 * so the client can evaluate the drop while cruise is still active and is
 * structurally immune to toggle-ordering bugs. The client's
 * `Supercruise.canDropToSignal` layers an `active` guard on top of this for the
 * HUD prompt; the drop-execution path calls this directly.
 */
export const shouldDropIntoSignal = (
  distKm: number | null,
  radiusKm: number | null,
  closingSpeedKmS = 0,
): boolean => {
  if (distKm === null || radiusKm === null) return false;
  const band = radiusKm + Math.max(0, closingSpeedKmS) * SIGNAL_DROP_LEAD_SEC;
  return distKm <= band;
};

/**
 * B7 opening story mission: a single deterministic, flagged MEMORIAL/derelict
 * signal near a known anchor (the client passes Mars's world position). Non-combat
 * (threat 0), never decays, and carries a fixed title/id so the tutorial objective
 * can target it. Pulls the Cenotaph / Forty-Seven Seconds lore.
 */
export const makeStorySignal = (nearPos: Vec3): SignalSource =>
  makeScriptedSignal(
    {
      id: 'story-cenotaph-vigil',
      kind: 'memorial',
      title: 'VERIFY: THE CENOTAPH WAYPOINT',
      threat: 0,
      seed: 4722049, // 47s + 2049 — the Silence
      markerOffsetKm: surfaceMarkerOffset({ x: 18_000, y: 4_000, z: -9_000 }, 70),
    },
    add(nearPos, { x: 18_000, y: 4_000, z: -9_000 }),
  );

/**
 * CAMPAIGN W1: a scripted, never-decaying signal at an exact position with an
 * optional AUTHORED carved record. Generalization of makeStorySignal — campaign
 * beats spawn as many of these as they declare; the verification machinery
 * (contradicts / verifySignal) runs on the authored record unchanged.
 */
export const makeScriptedSignal = (
  spec: {
    id: string;
    kind: SignalKind;
    title: string;
    threat: number;
    seed: number;
    markerOffsetKm?: Vec3;
    record?: { name: string; log: string | null };
    /** SHIP-EXPANSION: faction-scope this scripted pocket's hostiles. */
    faction?: FactionId;
    /** SHIP-EXPANSION: force this pocket's hostiles to a specific hull. */
    enemyType?: string;
  },
  pos: Vec3,
): SignalSource => ({
  id: spec.id,
  kind: spec.kind,
  pos: { ...pos },
  ...(spec.markerOffsetKm ? { markerOffsetKm: { ...spec.markerOffsetKm } } : {}),
  threatLevel: spec.threat,
  radiusKm: SIGNAL_RADIUS_KM,
  seed: spec.seed,
  decayT: Number.POSITIVE_INFINITY, // scripted signals never decay
  discovered: false,
  title: spec.title,
  ...(spec.record ? { record: spec.record } : {}),
  ...(spec.faction ? { faction: spec.faction } : {}),
  ...(spec.enemyType ? { enemyType: spec.enemyType } : {}),
});
